import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';

export const DEFAULT_LANGUAGE = 'zh-CN';
export const LANGUAGE_STORAGE_KEY = 'tenant-web:language';
export const LANGUAGE_CHANGE_EVENT = 'tenant-web:language-change';
export const SUPPORT_LANGUAGES = [
  { label: '中文', value: 'zh-CN' },
  { label: 'English', value: 'en-US' },
] as const;

export type SupportedLanguage = typeof SUPPORT_LANGUAGES[number]['value'];

function isSupportedLanguage(value: unknown): value is SupportedLanguage {
  return SUPPORT_LANGUAGES.some(item => item.value === value);
}

function getStorage(): Storage | undefined {
  if (typeof localStorage === 'undefined') return undefined;
  if (typeof localStorage.getItem !== 'function') return undefined;
  return localStorage;
}

const storedLanguage = getStorage()?.getItem(LANGUAGE_STORAGE_KEY);
let currentLanguage: SupportedLanguage = isSupportedLanguage(storedLanguage) ? storedLanguage : DEFAULT_LANGUAGE;
const listeners = new Set<() => void>();

/** syncDateLocale 让 dayjs 驱动的日期组件与站点语言保持一致。 */
function syncDateLocale(lang: SupportedLanguage): void {
  // antd locale 只负责组件文案；日历月份和星期仍由 dayjs locale 决定。
  dayjs.locale(lang === 'zh-CN' ? 'zh-cn' : 'en');
}

syncDateLocale(currentLanguage);

export function getCurrentLanguage(): SupportedLanguage {
  return currentLanguage;
}

/** updateCurrentLanguage 原子更新语言及其依赖的日期运行时。 */
function updateCurrentLanguage(lang: SupportedLanguage): boolean {
  if (currentLanguage === lang) return false;
  currentLanguage = lang;
  // federation 的 host/remote 可能各自持有 dayjs 实例，因此每个语言运行时都要同步一次。
  syncDateLocale(lang);
  return true;
}

/** 同一 bundle 的全部订阅者共享模块状态，外部事件也必须一次性广播给所有订阅者。 */
function notifyLanguageListeners(): void {
  listeners.forEach(listener => listener());
}

export function setCurrentLanguage(lang: string): void {
  if (!isSupportedLanguage(lang) || !updateCurrentLanguage(lang)) return;
  getStorage()?.setItem(LANGUAGE_STORAGE_KEY, lang);
  notifyLanguageListeners();
  if (typeof window !== 'undefined' && typeof window.dispatchEvent === 'function') {
    window.dispatchEvent(new CustomEvent<SupportedLanguage>(LANGUAGE_CHANGE_EVENT, { detail: lang }));
  }
}

export function subscribeLanguage(listener: () => void): () => void {
  listeners.add(listener);

  const syncLanguage = (lang: unknown) => {
    if (!isSupportedLanguage(lang) || !updateCurrentLanguage(lang)) return;
    // federation 外部事件只会更新一次模块状态，但必须唤醒本模块的全部订阅者。
    notifyLanguageListeners();
  };
  const handleLanguageChange = (event: Event) => {
    syncLanguage((event as CustomEvent<SupportedLanguage>).detail);
  };
  const handleStorage = (event: StorageEvent) => {
    if (event.key === LANGUAGE_STORAGE_KEY) syncLanguage(event.newValue);
  };

  if (typeof window !== 'undefined') {
    window.addEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
    window.addEventListener('storage', handleStorage);
  }
  return () => {
    listeners.delete(listener);
    if (typeof window !== 'undefined') {
      window.removeEventListener(LANGUAGE_CHANGE_EVENT, handleLanguageChange);
      window.removeEventListener('storage', handleStorage);
    }
  };
}
